import { ReactElement, useCallback, useMemo, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Stack, Tooltip } from '@mui/material';
import { Visibility as ShowIcon, Edit as EditIcon } from '@mui/icons-material';

import { ItemExtended } from '~services/item/types';
import { useAppSelector } from '~hooks/useAppSelector';
import { selectAuth } from '~store/slices/auth';
import { isOnlyReadUser, isOnlyCreatorOrder } from '~utils/user';
import { replaceParamsPath } from '~utils/router';
import { PATHS } from '~constants/paths';

import { useDashboardVariantV2GiverContentInTransitTabContext } from '../../Context';
import { ITEM_KEY_ID } from '../../utils';

import { DashboardVariantV2GiverContentInTransitTabDataTableIconButton } from './styles';

export interface DashboardVariantV2GiverContentInTransitTabDataTableActionsProps {
  data: ItemExtended;
}

const DashboardVariantV2GiverContentInTransitTabDataTableActions = ({
  data,
}: DashboardVariantV2GiverContentInTransitTabDataTableActionsProps): ReactElement => {
  const navigate = useNavigate();
  const { user } = useAppSelector(selectAuth);

  const isUserOnlyReader = useMemo(() => isOnlyReadUser(user), [user]);
  const isUserOnlyCreatorOrder = useMemo(
    () => isOnlyCreatorOrder(user),
    [user],
  );

  const {
    dialogs: {
      detailsOrder: { handleOpen: handleOpenDialogDetailsOrder },
    },
  } = useDashboardVariantV2GiverContentInTransitTabContext();

  const handleShow = useCallback(() => {
    handleOpenDialogDetailsOrder({
      orderId: data[ITEM_KEY_ID],
      currentTab: null,
    });
  }, [data, handleOpenDialogDetailsOrder]);

  const enabledEdit = useMemo(
    () => !isUserOnlyReader && !isUserOnlyCreatorOrder && data.canModify,
    [isUserOnlyReader, isUserOnlyCreatorOrder, data.canModify],
  );

  const handleEdit = useCallback(() => {
    navigate(
      replaceParamsPath(PATHS.ORDERS.EDIT, { ':orderId': data[ITEM_KEY_ID] }),
    );
  }, [data, navigate]);

  return (
    <Stack direction="row" spacing={0.75} justifyContent="center">
      <Tooltip title="Ver" arrow>
        <DashboardVariantV2GiverContentInTransitTabDataTableIconButton
          color="success"
          variant="outlined"
          onClick={handleShow}
        >
          <ShowIcon />
        </DashboardVariantV2GiverContentInTransitTabDataTableIconButton>
      </Tooltip>

      {enabledEdit && (
        <Tooltip title="Editar" arrow>
          <DashboardVariantV2GiverContentInTransitTabDataTableIconButton
            color="info"
            variant="outlined"
            onClick={handleEdit}
          >
            <EditIcon />
          </DashboardVariantV2GiverContentInTransitTabDataTableIconButton>
        </Tooltip>
      )}
    </Stack>
  );
};

export default memo(DashboardVariantV2GiverContentInTransitTabDataTableActions);
